import z from "zod";

import { ArtSectionsSchema } from "@features/home-art-sections/model/schemas";
import { AboutMoreSchema } from "@features/home-more-about/model";

import { BackgroundMediaSchema } from "@shared/ui/background-media";

import { CulturalObjectSchema } from "@/_pages/cultural-objects";
import { EventSchema } from "@/_pages/event/model";

import { createComponentSchemas } from "../utils";
import {
  Button,
  ButtonBaseComponent,
  CityIcons,
  ColorIconSchema,
  InfoListItem,
} from "./common.schema";
import { StrapiMediaSchema } from "./media.schema";

const EventsSliderSchema = z.object({
  isVisible: z.boolean().optional().default(true),
  events: z.array(EventSchema),
});

export const EventsSliderSection = createComponentSchemas(
  "sections.events-slider",
  EventsSliderSchema,
);

export const AboutMoreSection = createComponentSchemas(
  "sections.about-more",
  AboutMoreSchema,
);

const MainAboutSectionBaseSchema = z.object({
  isVisible: z.boolean().optional().default(true),
  subtitle: z.string().min(1),
  title: z.string().min(1),
  background: BackgroundMediaSchema.base,
  buttons: Button.base.array().min(1).optional().nullable(),
});

export const MainAboutSection = createComponentSchemas(
  "sections.main-about-section",
  MainAboutSectionBaseSchema,
);

export const SliderCardBaseSchema = z.object({
  id: z.number().optional(),
  title: z.string().min(1),
  description: z.string().optional().nullable(),
  location: z.string().optional().nullable(),
  time: z.string().optional().nullable(),
  link: z.string().optional().nullable(),
  icon: ColorIconSchema,
  image: StrapiMediaSchema.optional().nullable(),
});

export const SliderCard = createComponentSchemas(
  "ui.slider-card",
  SliderCardBaseSchema,
);

const SliderVariantBaseSchema = z.object({
  id: z.number().int().positive(),
  subtitle: z.string().min(1),
  title: z.string().min(1),
  description: z.string().optional().nullable(),
  button: ButtonBaseComponent.base.optional().nullable(),
  buttonIcon: ColorIconSchema.optional().nullable(),
  sliderCards: SliderCard.base.array().min(0),
});

export const SliderVariant = createComponentSchemas(
  "ui.slider-section",
  SliderVariantBaseSchema,
);

const SliderSectionBaseSchema = z.object({
  isVisible: z.boolean().optional().default(true),
  sliders: SliderVariant.base.array().min(0).max(2),
});

export const SliderSection = createComponentSchemas(
  "sections.slider-section",
  SliderSectionBaseSchema,
);

const CulturalObjectsSliderBaseSchema = z.object({
  isVisible: z.boolean().optional().default(true),
  title: z.string(),
  subtitle: z.string(),
  id: z.number(),
  cultural_object: CulturalObjectSchema,
});

export const CulturalObjectsSlider = createComponentSchemas(
  "sections.cultural-objects-slider",
  CulturalObjectsSliderBaseSchema,
);

const HowToGetSectionBaseSchema = z.object({
  isVisible: z.boolean().optional().default(true),
  subtitle: z.string().min(1),
  title: z.string().min(1),
  items: InfoListItem.base.array().min(1),
  button: Button.base.optional().nullable(),
  cities: CityIcons.base.array().min(1),
});

export const HowToGetSection = createComponentSchemas(
  "sections.how-to-get-section",
  HowToGetSectionBaseSchema,
);

// DTO схема для single type how-to-get-section
export const HowToGetSectionDtoSchema = z.object({
  data: HowToGetSectionBaseSchema,
});

export const ArtSectionsSection = createComponentSchemas(
  "sections.art-sections",
  ArtSectionsSchema,
);

// Типы:
export type CulturalObjectsSliderBase = z.infer<
  typeof CulturalObjectsSlider.base
>;
export type CulturalObjectsSliderDynamic = z.infer<
  typeof CulturalObjectsSlider.dynamic
>;
export type EventsSliderSectionBase = z.infer<typeof EventsSliderSection.base>;
export type EventsSliderSectionDynamic = z.infer<
  typeof EventsSliderSection.dynamic
>;

export type AboutMoreSectionBase = z.infer<typeof AboutMoreSection.base>;
export type AboutMoreSectionDynamic = z.infer<typeof AboutMoreSection.dynamic>;

export type ArtSectionsSectionBase = z.infer<typeof ArtSectionsSection.base>;
export type ArtSectionsSectionDynamic = z.infer<
  typeof ArtSectionsSection.dynamic
>;

export type MainAboutSectionBase = z.infer<typeof MainAboutSection.base>;
export type MainAboutSectionDynamic = z.infer<typeof MainAboutSection.dynamic>;

export type SliderCardBase = z.infer<typeof SliderCard.base>;
export type SliderCardDynamic = z.infer<typeof SliderCard.dynamic>;

export type SliderVariantBase = z.infer<typeof SliderVariant.base>;
export type SliderVariantDynamic = z.infer<typeof SliderVariant.dynamic>;

export type SliderSectionBase = z.infer<typeof SliderSection.base>;
export type SliderSectionDynamic = z.infer<typeof SliderSection.dynamic>;

export type HowToGetSectionBase = z.infer<typeof HowToGetSection.base>;
export type HowToGetSectionDynamic = z.infer<typeof HowToGetSection.dynamic>;
